import { useContext } from "react";
import { OverlayTriggerStateContext } from "react-aria-components";

/**
 * Shared open-state bridge for dialog wrappers (PopupDialog, CustomDialog…)
 * that sit on top of the controlled `Modal` but should also work when wrapped
 * in a react-aria-components `DialogTrigger`.
 *
 * Returns `isOpen` derived from whichever owns it (trigger > controlled prop)
 * and a `handleDidDismiss` that closes the trigger state (if any) and forwards
 * to the optional `onClose` callback.
 */
export interface UseDialogTriggerOpenStateOptions {
  isOpen?: boolean;
  onClose?: () => void;
}

export interface UseDialogTriggerOpenStateResult {
  isOpen: boolean;
  handleDidDismiss: () => void;
}

export function useDialogTriggerOpenState({
  isOpen,
  onClose
}: UseDialogTriggerOpenStateOptions): UseDialogTriggerOpenStateResult {
  const triggerState = useContext(OverlayTriggerStateContext);

  return {
    isOpen: triggerState ? triggerState.isOpen : (isOpen ?? false),
    handleDidDismiss: () => {
      triggerState?.close();
      onClose?.();
    }
  };
}
